import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Page,
    Layout,
    Card,
    IndexTable,
    Badge,
    Button,
    useIndexResourceState,
    Text,
    SkeletonBodyText,
    EmptyState,
    Icon,
    Tooltip,
    InlineStack,
    ButtonGroup,
    Box
} from '@shopify/polaris';
import {
    PlusIcon,
    ViewIcon,
    CalendarIcon,
    PersonIcon,
    StatusActiveIcon,
    ListBulletedIcon,
    LocationIcon,
} from '@shopify/polaris-icons';
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';

// Custom ToggleSwitch component
const ToggleSwitch = ({ checked, onChange }) => (
    <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
        />
        <span className="slider" />
    </label>
);

const Tasklist = () => {
    const axiosClient = useAxiosClient();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // States
    const [searchValue, setSearchValue] = useState('');
    const [tasklists, setTasklists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch tasklist data
    useEffect(() => {
        const fetchTasklists = async () => {
            try {
                setIsLoading(true);
                const response = await axiosClient.get('/get_tasklists');
                if (response.data.success) {
                    setTasklists(response.data.data);
                }
            } catch (error) {
                // Error handled silently
            } finally {
                setIsLoading(false);
            }
        };
        fetchTasklists();
    }, []);

    let filteredTasklists = [...tasklists];

    // Filter by search value
    if (searchValue) {
        filteredTasklists = filteredTasklists.filter(t =>
            t.name.toLowerCase().includes(searchValue.toLowerCase())
        );
    }

    const { selectedResources, handleSelectionChange } = useIndexResourceState(filteredTasklists);

    const EmptyStateComponent = () => (
        <EmptyState
            heading={t("create_your_first_task_list")}
            action={{
                content: t("create_task_list"),
                icon: PlusIcon,
                onAction: () => navigate('/Tasklist/AddTasklist'),
            }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
            <p>{t("start_organizing_tasks_for_your_team_and_locations")}</p>
        </EmptyState>
    );

    const LoadingState = () => (
        <Card sectioned>
            <SkeletonBodyText lines={6} />
        </Card>
    );

    // Navigate to edit page
    const handleTasklistClick = (tasklistId) => {
        navigate(`/Tasklist/EditTasklist/${tasklistId}`);
    };

    // Navigate to details page
    const handleViewDetails = (tasklistId, event) => {
        event.stopPropagation();
        navigate(`/Tasklist/TasklistDetails/${tasklistId}`);
    };

    // Toggle active/completed status
    const handleToggleStatus = async (tasklistId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'Active' ? 'Deactive' : 'Active';

            // Update optimistically
            setTasklists(prevTasklists =>
                prevTasklists.map(tasklist =>
                    tasklist.id === tasklistId
                        ? { ...tasklist, status: newStatus }
                        : tasklist
                )
            );

            const response = await axiosClient.post(`/update-tasklist-status/${tasklistId}`, {
                status: newStatus,
            });

            if (!response.data.success) {
                // Revert on failure
                setTasklists(prevTasklists =>
                    prevTasklists.map(tasklist =>
                        tasklist.id === tasklistId
                            ? { ...tasklist, status: currentStatus }
                            : tasklist
                    )
                );
            }
        } catch (error) {
            // Revert on error
            setTasklists(prevTasklists =>
                prevTasklists.map(tasklist =>
                    tasklist.id === tasklistId
                        ? { ...tasklist, status: currentStatus }
                        : tasklist
                )
            );
        }
    };

    // Get timeline display with colored icons
    const getTimelineDisplay = (timeline) => {
        const timelines = timeline ? timeline.split(',') : [];

        return (
            <InlineStack gap="200">
                {timelines.includes('daily') && <Badge tone="success">{t("daily")}</Badge>}
                {timelines.includes('weekly') && <Badge tone="info">{t("weekly")}</Badge>}
                {timelines.includes('monthly') && <Badge tone="attention">{t("monthly")}</Badge>}
                {timelines.length === 0 && <Text color="subdued">{t("no_timeline")}</Text>}
            </InlineStack>
        );
    };

    // Calculate task completion percentage
    const getCompletionPercentage = (tasklist) => {
        if (!tasklist.tasks || tasklist.tasks.length === 0) return 0;

        const completedTasks = tasklist.tasks.filter(task => task.status === 'completed').length;
        return Math.round((completedTasks / tasklist.tasks.length) * 100);
    };

    return (
        <Page
            fullWidth
            title={t("tasklists")}
            primaryAction={{
                content: t("create_task_list"),
                icon: PlusIcon,
                primary: true,
                onClick: () => navigate('/Tasklist/AddTasklist'),
            }}
        >
            <Layout>
                <Layout.Section>
                    <Card padding="0">
                        {isLoading ? (
                            <LoadingState />
                        ) : filteredTasklists.length === 0 ? (
                            <EmptyStateComponent />
                        ) : (
                            <IndexTable
                                resourceName={{ singular: 'tasklist', plural: 'tasklists' }}
                                itemCount={filteredTasklists.length}
                                selectedItemsCount={selectedResources.length}
                                onSelectionChange={handleSelectionChange}
                                headings={[
                                    {
                                        title: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{margin:'0px'}}>
                                                <Icon source={StatusActiveIcon} />
                                                </div>
                                                <Text>{t("status")}</Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{margin:'0px'}}>
                                                <Icon source={ListBulletedIcon} />
                                                </div>
                                                <Text>{t("tasklist")}</Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{margin:'0px'}}>
                                                <Icon source={CalendarIcon} />
                                                </div>
                                                <Text>{t("timeline")}</Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{margin:'0px'}}>
                                                <Icon source={LocationIcon} />
                                                </div>
                                                <Text>{t("locations")}</Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <div style={{margin:'0px'}}>
                                                <Icon source={PersonIcon} />
                                                </div>
                                                        <Text>{t("assigned_to")}</Text>
                                            </div>
                                        ),
                                    },
                                    {
                                        title: <Text>{t("actions")}</Text>,
                                    },
                                ]}
                                selectable={false}
                            >
                                {filteredTasklists.map((tasklist, index) => {
                                    const completionPercentage = getCompletionPercentage(tasklist);

                                    return (
                                        <IndexTable.Row
                                            id={tasklist.id.toString()}
                                            key={tasklist.id}
                                            selected={selectedResources.includes(tasklist.id.toString())}
                                            position={index}
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => handleTasklistClick(tasklist.id)}
                                        >
                                            <IndexTable.Cell onClick={(e) => e.stopPropagation()}>
                                                <ToggleSwitch
                                                    checked={tasklist.status === 'Active'}
                                                    onChange={() => handleToggleStatus(tasklist.id, tasklist.status)}
                                                />
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Text variant="bodyMd" fontWeight="bold">
                                                        {tasklist.name}
                                                    </Text>
                                                    {tasklist.tasks && (
                                                        <Text color="subdued">
                                                            ({tasklist.tasks.length} {t("tasks")})
                                                        </Text>
                                                    )}
                                                </div>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                {getTimelineDisplay(tasklist.timeline)}
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Tooltip content={tasklist.locations}>
                                                    <Text truncate>{tasklist.locations}</Text>
                                                </Tooltip>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell>
                                                <Badge tone="info">{tasklist.assigned_to}</Badge>
                                            </IndexTable.Cell>
                                            <IndexTable.Cell onClick={(e) => e.stopPropagation()}>
                                                <ButtonGroup>
                                                    <Button
                                                        icon={ViewIcon}
                                                        onClick={(e) => handleViewDetails(tasklist.id, e)}
                                                        size="slim"
                                                        variant="primary"
                                                    >
                                                        {t("view_details")}
                                                    </Button>
                                                </ButtonGroup>
                                            </IndexTable.Cell>
                                        </IndexTable.Row>
                                    );
                                })}
                            </IndexTable>
                        )}
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default Tasklist;